import {
  Attachment,
  CardContainer,
  isImageOr3dModel,
  isPdfOrFile,
  MenuButton,
  processAttachment,
} from "@msys/ui";
import {
  Add as AddIcon,
  AttachFile as AttachFileIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  List,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { unionBy } from "lodash-es";
import React from "react";
import { GalleryGrid } from "../images/GalleryGrid.js";
import { ViewMode, ViewModeMenuItem } from "../ViewModeMenuItem.js";
import { DeleteButton } from "./DeleteButton.js";
import { FileListItemButton } from "./FileListItemButton.js";
import { useFileInput } from "./useFileInput.js";

interface FileBoxItem extends Attachment {
  id: string;
}

interface ViewModeWithItemAction {
  viewMode: ViewMode;
  renderItemActionButton?: (file: FileBoxItem) => React.ReactNode;
}

interface ReadOnlyProps {
  readOnly: true;
  title?: string;
  attachments: Array<FileBoxItem> | undefined;
  isInitiallyClosed?: boolean;
  showImagesInGallery?: boolean;
  gridColumns?: number;
  noItemsText?: string;
  renderItemTitleBadge?: (file: FileBoxItem) => React.ReactNode;
}

type ExtractUnion<T, U> = T extends { viewMode: infer V }
  ? V extends U
    ? T
    : never
  : never;

interface Props extends Omit<ReadOnlyProps, "readOnly" | "customViewModes"> {
  readOnly?: boolean | undefined;
  loading?: boolean;
  handleDelete: (id: string) => Promise<any> | void;
  renderDeleteButton?: (button: React.ReactNode) => React.ReactNode;
  handleAdd: (files: File[]) => Promise<any> | void;
  renderAddButton?: (button: React.ReactNode) => React.ReactNode;
  customViewModes?: ExtractUnion<
    ViewModeWithItemAction,
    Exclude<ViewMode, "delete">
  >[];
}

const defaultRenderButton = (button: React.ReactNode) => button;

export function FilesBox({
  title,
  attachments = [],
  isInitiallyClosed,
  showImagesInGallery = true,
  gridColumns = 3,
  noItemsText,
  renderItemTitleBadge,
  ...props
}: ReadOnlyProps | Props) {
  const { t } = useTranslate(["FilesBoxTable"]);

  const { open } = useFileInput();

  const typedAttachments = attachments.map(processAttachment);
  const images = showImagesInGallery
    ? typedAttachments.filter(isImageOr3dModel)
    : [];
  const files = showImagesInGallery
    ? typedAttachments.filter(isPdfOrFile)
    : typedAttachments;

  const handleAddImage = !props.readOnly
    ? async () => {
        open({
          accept: "image/*,.heic,.heif",
          multiple: true,
          onComplete: files => {
            props.handleAdd(files);
          },
        });
      }
    : undefined;

  const handleDeleteFile = !props.readOnly
    ? async (attachment: { id: string }) => {
        await props.handleDelete(attachment.id);
      }
    : undefined;

  const viewModes = React.useMemo(() => {
    const viewModes: ViewModeWithItemAction[] = [];

    viewModes.push({ viewMode: null });
    if (!props.readOnly) {
      if (attachments.length > 0) {
        viewModes.push({
          viewMode: "delete",
          renderItemActionButton: file =>
            (props.renderDeleteButton ?? defaultRenderButton)(
              <DeleteButton
                handleDelete={async () => {
                  await props.handleDelete(file.id);
                }}
                disabled={!!props.loading}
              />
            ),
        });
      }

      if (props.customViewModes && props.customViewModes.length > 0) {
        return unionBy(props.customViewModes, viewModes, "viewMode");
      }
    }

    return viewModes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    attachments.length,
    props.readOnly,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    !props.readOnly && props.customViewModes,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    !props.readOnly && props.renderDeleteButton,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    !props.readOnly && props.handleDelete,
  ]);

  const [viewMode, setViewMode] = React.useState<ViewMode>(
    viewModes[0].viewMode
  );

  const currentViewMode = viewModes.find(vm => vm.viewMode === viewMode);

  return (
    <CardContainer
      Icon={<AttachFileIcon />}
      title={title ?? t("Files", { ns: "FilesBoxTable" })}
      itemCount={attachments.length}
      isInitiallyClosed={isInitiallyClosed}
      ActionButton={
        <Stack
          direction={"row"}
          spacing={1 / 2}
          sx={{
            alignItems: "center",
          }}
        >
          {!props.readOnly &&
            (props.renderAddButton ?? defaultRenderButton)(
              <Tooltip title={t("Upload file")}>
                <span>
                  <IconButton
                    aria-label={t("Upload file")}
                    color="primary"
                    disabled={!!props.loading}
                    onClick={() => {
                      open({
                        accept: "*",
                        multiple: true,
                        onComplete: files => {
                          props.handleAdd(files);
                        },
                      });
                    }}
                    size="small"
                  >
                    <AddIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          {viewModes.length > 1 && (
            <MenuButton>
              <ViewModeMenuItem
                allowedModes={viewModes.map(vm => vm.viewMode)}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />
            </MenuButton>
          )}
        </Stack>
      }
    >
      {!props.readOnly && props.loading && <LinearProgress />}
      <Stack direction="column" spacing={0}>
        {images.length > 0 && (
          <Box
            sx={{
              padding: 1,
            }}
          >
            <GalleryGrid
              images={images}
              columns={gridColumns}
              handleAdd={handleAddImage}
              handleDelete={handleDeleteFile}
              showDeleteButton={
                Boolean(handleDeleteFile) && viewMode === "delete"
              }
            />
          </Box>
        )}
        {files.length > 0 && (
          <List disablePadding>
            {files.map(file => (
              <FileListItemButton
                key={file.id}
                file={file}
                titleBadge={renderItemTitleBadge?.(file)}
                secondaryAction={currentViewMode?.renderItemActionButton?.(
                  file
                )}
              />
            ))}
          </List>
        )}
      </Stack>
      {attachments.length === 0 && (
        <Stack
          direction={"column"}
          spacing={1}
          sx={{
            alignItems: "center",
            padding: 1,
          }}
        >
          {noItemsText && (
            <Typography variant="caption" color="gray" align="center">
              {noItemsText}
            </Typography>
          )}
          {!props.readOnly &&
            (props.renderAddButton ?? defaultRenderButton)(
              <Button
                startIcon={<AddIcon />}
                color="secondary"
                variant="text"
                disabled={!!props.loading}
                onClick={() => {
                  open({
                    accept: "*",
                    multiple: true,
                    onComplete: files => {
                      props.handleAdd(files);
                    },
                  });
                }}
              >
                {t("Upload file", { ns: "FilesBoxTable" })}
              </Button>
            )}
        </Stack>
      )}
    </CardContainer>
  );
}
